import { action, computed, observable, makeObservable, when } from 'mobx';
import * as Sentry from '@sentry/browser';
import numeral from 'numeral';
import { sum as sumArray } from 'lodash';
import epaxios from '@/utils/epaxios';
import logger from '@/utils/logger';
import RootStore from './RootStore';
import { permissions } from '@/constants';
import { AlertLevels, ComponentTierName } from '@/constants/enums';
import { checkTierPermission } from '@/utils/util-functions';
import { StationRiskMock } from '@/constants/StationRiskMock';
import { MonthDaysDeltaMock } from '@/constants/MonthDaysDeltaMock';
import { onCurrentStationChange } from '@/utils/store';
import { SocketEvent } from '@/types/socket-events.types';
import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';

export default class StationRiskStore {
  rootStore: typeof RootStore;
  @observable monthOverMonth: MonthOverMonth;
  @observable stationCapacity: StationCapacity;
  @observable stationRiskGauge: StationRiskGauge;
  @observable mockFlow: number;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.monthOverMonth = new MonthOverMonth();
    this.stationCapacity = new StationCapacity();
    this.stationRiskGauge = new StationRiskGauge();
    onCurrentStationChange(this, this.reset);
  }

  @action get = async () => {
    if (this.isLoading || this.isReady) return;
    try {
      this.setIsLoading(true);
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await this.loadMonthOverMonthData(stationId);
      await this.loadStationCapacityData(stationId);
      await this.loadGaugeData();
    } catch (e) {
      Sentry.captureException(new Error(`Error when loading Station Risk data: ${e}`));
    } finally {
      this.setIsLoading(false);
    }
  };

  setIsLoading = (isLoading: boolean) => {
    this.monthOverMonth.setIsLoading(isLoading);
    this.stationCapacity.setIsLoading(isLoading);
    this.stationRiskGauge.setIsLoading(isLoading);
  };

  @computed get isLoading() {
    return this.monthOverMonth.isLoading || this.stationCapacity.isLoading || this.stationRiskGauge.isLoading;
  }

  @computed get isReady() {
    return this.monthOverMonth.isReady && this.stationCapacity.isReady && this.stationRiskGauge.isReady;
  }

  @computed get hasError() {
    return this.monthOverMonth.hasError || this.stationCapacity.hasError || this.stationRiskGauge.hasError;
  }

  @action reset = () => {
    this.stationCapacity = new StationCapacity();
    this.monthOverMonth = new MonthOverMonth();
    this.stationRiskGauge = new StationRiskGauge();
  };

  @action updateMockFlow = val => {
    this.mockFlow = val;
  };

  @action loadMonthOverMonthData = async (stationId: string) => {
    try {
      const {
        data: { peakSupplyFlowDelta, availableSupplyFlowDelta },
      } = checkTierPermission(ComponentTierName.StationRiskMock, this.rootStore.stationStore)
        ? MonthDaysDeltaMock
        : await epaxios.get(`/value-dashboard/station-risk/station/${stationId}/30-days-delta`);
      this.monthOverMonth.setData({
        peakSupplyFlowDelta: peakSupplyFlowDelta,
        availableSupplyFlowDelta: availableSupplyFlowDelta,
      });
    } catch (e) {
      logger(`Error when fetching Station risk - month over month data for station ${stationId}: ${e}`);
      Sentry.captureException(
        new Error(`Error when fetching Station risk - month over month data for station ${stationId}: ${e}`),
      );
      this.monthOverMonth.hasError = true;
    } finally {
      this.monthOverMonth.isReady = true;
    }
  };

  @action loadGaugeData = async () => {
    await when(() => this.rootStore.stationStore.currentStation.devices.length > 0, { timeout: 10000 });
    const activeDevicesIds = new Set(this.stationCapacity.activeDevices.map(device => device.deviceId));
    const activePoweredDevice = this.rootStore.stationStore.currentStation.devices.find(device => {
      return activeDevicesIds.has(device.deviceId) && getPoweredOnState(device.poweredOn);
    });
    this.stationRiskGauge.loadData(this.stationCapacity, !activePoweredDevice);
  };

  @action loadStationCapacityData = async stationId => {
    try {
      if (this.rootStore.permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD_STATION_RISK)) {
        const {
          data: { peakSupplyFlow, devices, validationErrorName, msg },
        } = checkTierPermission(ComponentTierName.StationRiskMock, this.rootStore.stationStore)
          ? StationRiskMock
          : await epaxios.get(`/value-dashboard/station-risk/station/${stationId}/station-capacity`);
        if (validationErrorName) {
          throw new Error(msg);
        }
        this.stationCapacity.setData({ peakSupplyFlow: peakSupplyFlow, devices: devices });
      }
    } catch (e) {
      logger(`Error when fetching Station risk - station capacity data for station ${stationId}: ${e}`);
      Sentry.captureException(
        new Error(`Error when fetching Station risk - station capacity data for station ${stationId}: ${e}`),
      );
      this.stationCapacity.hasError = true;
    } finally {
      this.stationCapacity.isReady = true;
    }
  };

  @action handleDeviceUpdateEvent = (payload?: SocketEvent['payload']) => {
    if (!payload) {
      return;
    }
    if ('connectivity' in payload) {
      this.reset();
    }
    if ('poweredOn' in payload) {
      this.loadGaugeData();
    }
  };

  @action handleAlertCreatedEvent = (payload?: SocketEvent['payload']) => {
    if (payload?.severity === AlertLevels.Shutdown) {
      this.reset();
    }
  };

  @action handleAlertUpdatedEvent = (payload?: SocketEvent['payload']) => {
    if (payload && 'status' in payload && payload.status !== 'open') {
      const alert = this.rootStore.alertStore.alerts?.find(alert => alert.id === payload?.id);
      if (alert && alert.severity === AlertLevels.Shutdown) {
        this.reset();
      }
    }
  };
}

class StationCapacity {
  @observable isReady = false;
  @observable hasError = false;
  @observable peakSupplyFlow = null;
  @observable devices = [];
  @observable isLoading: boolean;

  constructor() {
    makeObservable(this);
  }

  formatValueToFixed = num => {
    return numeral(num).format('0[.]00');
  };

  @action setData = data => {
    try {
      this.peakSupplyFlow = data.peakSupplyFlow;
      this.devices = data.devices;
      if (!this.devices) {
        throw new Error('No Devices Received');
      }
      this.devices.map(d => (d['flowAfterDegradation'] = d.maxFlow - d.flowDegradation));
    } catch (e) {
      console.warn(e);
      Sentry.captureException(new Error(`Error when setting Station risk - station capacity data: ${e}`));
      this.hasError = true;
    } finally {
      this.isReady = true;
    }
  };

  @action setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action reset = () => {
    this.devices = [];
    this.peakSupplyFlow = null;
    this.hasError = false;
    this.isLoading = false;
  };

  @computed get disabledDevices() {
    return this.devices?.filter(device => device.status !== 'active');
  }

  @computed get activeDevices() {
    return this.devices?.filter(device => device.status === 'active');
  }

  @computed get sumOfDisabledFlow() {
    return this.formatValueToFixed(sumArray(this.disabledDevices?.map(device => device.flowAfterDegradation)));
  }

  @computed get totalDegradation() {
    return this.formatValueToFixed(sumArray(this.devices?.map(d => d.flowDegradation)));
  }

  @computed get totalFlow() {
    return sumArray(this.devices?.map(device => device.maxFlow));
  }

  @computed get maxAvailableFlow() {
    return this.formatValueToFixed(this.totalFlow - this.sumOfDisabledFlow - this.totalDegradation);
  }
}

class StationRiskGauge {
  @observable isReady = false;
  @observable hasError = false;
  @observable isLoading = false;
  @observable gaugeRiskPercentage = 0;
  @observable noActiveDevices = false;

  constructor() {
    makeObservable(this);
  }

  @action setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  loadData(stationCapacity: StationCapacity, noActivePoweredCompressor: boolean) {
    if (!stationCapacity.isReady) return;
    if (stationCapacity.hasError) {
      this.hasError = true;
      this.isReady = true;
      return;
    }
    try {
      if (noActivePoweredCompressor) {
        this.noActiveDevices = true;
        return;
      }
      const activeDevices = stationCapacity.activeDevices;
      const totalActiveFlow = sumArray(activeDevices.map(device => device.flowAfterDegradation));
      const delta = totalActiveFlow - stationCapacity.peakSupplyFlow;
      const numOfDevicesWithMoreFlowThenDelta = activeDevices.filter(
        device => device.flowAfterDegradation > delta,
      ).length;
      this.gaugeRiskPercentage = +((numOfDevicesWithMoreFlowThenDelta / activeDevices.length) * 100).toFixed(0);
    } catch (e) {
      this.hasError = true;
      console.error(`Error while loading Gauge data: ${e}`);
      Sentry.captureException(new Error(`Error while loading Gauge data: ${e}`));
    } finally {
      this.isReady = true;
    }
  }
}

class MonthOverMonth {
  @observable isReady = false;
  @observable hasError = false;
  @observable peakSupplyFlowDelta: number;
  @observable availableSupplyFlowDelta: number;
  @observable isLoading = false;

  constructor() {
    makeObservable(this);
    this.peakSupplyFlowDelta = 0;
    this.availableSupplyFlowDelta = 0;
  }

  @action setData = data => {
    try {
      this.peakSupplyFlowDelta = data.peakSupplyFlowDelta;
      this.availableSupplyFlowDelta = data.availableSupplyFlowDelta;
    } catch (e) {
      this.hasError = true;
    } finally {
      this.isReady = true;
    }
  };

  @action setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action reset = () => {
    this.peakSupplyFlowDelta = null;
    this.availableSupplyFlowDelta = null;
    this.hasError = false;
    this.isLoading = false;
  };
}
